import React, {
  Fragment,
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";

import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import { OrgPreferences } from "@safetyculture/s12-apis-connect-web/s12/notifications/v1/notification_org_preferences_pb";

import { getOrgPreferences } from "api/endpoints/notifications";
import { callOptionsHeaders } from "api/helpers";
import { SnackbarContext } from "context/snackbar-context";

type ChannelState = {
  value: boolean;
  channelEnabled: boolean;
};

type NotificationItem = {
  id: string;
  label: string;
  email?: ChannelState;
  push?: ChannelState;
  sms?: ChannelState;
  locked: boolean;
};

type NotificationSection = {
  id: string;
  label: string;
  items: { [itemId: string]: NotificationItem };
};

type OrgNotificationSettings = {
  [sectionId: string]: NotificationSection;
};

type ChannelStatus = {
  email: boolean;
  push: boolean;
  sms: boolean;
};

const stripChannelPrefix = (id: string) =>
  id.replace(/^(section|switch)_(email|push|sms)_/, "");

const transformOrgPreferences = (
  preferences: OrgPreferences,
): { settings: OrgNotificationSettings; channelStatus: ChannelStatus } => {
  const channelStatus: ChannelStatus = {
    email: preferences.email?.channel?.value ?? false,
    push: preferences.push?.channel?.value ?? false,
    sms: preferences.sms?.channel?.value ?? false,
  };

  const settings: OrgNotificationSettings = {};

  for (const [channel, channelData, channelEnabled] of [
    ["email", preferences.email, channelStatus.email],
    ["push", preferences.push, channelStatus.push],
    ["sms", preferences.sms, channelStatus.sms],
  ] as const) {
    for (const section of channelData?.sections ?? []) {
      const sectionId = section.section?.id;
      if (!sectionId) continue;
      const normalizedId = stripChannelPrefix(sectionId);

      if (!settings[normalizedId]) {
        settings[normalizedId] = {
          id: normalizedId,
          label: section.section?.label ?? normalizedId,
          items: {},
        };
      }

      for (const item of section.items) {
        const itemId = item.item?.id;
        if (!itemId) continue;

        if (!settings[normalizedId].items[itemId]) {
          settings[normalizedId].items[itemId] = {
            id: itemId,
            label: item.item?.label ?? itemId,
            locked: item.locked ?? false,
          };
        }

        settings[normalizedId].items[itemId][channel] = {
          value: item.item?.value ?? false,
          channelEnabled,
        };
      }
    }
  }

  return { settings, channelStatus };
};

interface OrgNotificationsTabProps {
  roleId: string;
  regionCode: string;
}

const OrgNotificationsTab: FunctionComponent<OrgNotificationsTabProps> = ({
  roleId,
  regionCode,
}) => {
  const [settings, setSettings] = useState<OrgNotificationSettings>({});
  const [channelStatus, setChannelStatus] = useState<ChannelStatus | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { showError } = useContext(SnackbarContext);

  useEffect(() => {
    setIsLoading(true);
    getOrgPreferences({ orgId: roleId }, callOptionsHeaders(regionCode, roleId))
      .then((res) => {
        if (res.preferences) {
          const { settings: newSettings, channelStatus: newChannelStatus } =
            transformOrgPreferences(res.preferences);
          setSettings(newSettings);
          setChannelStatus(newChannelStatus);
        }
      })
      .catch(showError)
      .finally(() => setIsLoading(false));
  }, [roleId, regionCode, showError]);

  const displayChannelIcon = (channel?: ChannelState) => {
    if (!channel) return null;

    if (channel.value) {
      return (
        <CheckCircleIcon
          color={channel.channelEnabled ? "primary" : "disabled"}
        />
      );
    }
    return <CancelIcon color={channel.channelEnabled ? "error" : "disabled"} />;
  };

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold" }}>Category</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Notification type</TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="center">
              Email
              {channelStatus && (
                <Chip
                  label={channelStatus.email ? "On" : "Off"}
                  color={channelStatus.email ? "success" : "default"}
                  size="small"
                  sx={{ ml: 1 }}
                />
              )}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="center">
              Push
              {channelStatus && (
                <Chip
                  label={channelStatus.push ? "On" : "Off"}
                  color={channelStatus.push ? "success" : "default"}
                  size="small"
                  sx={{ ml: 1 }}
                />
              )}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="center">
              SMS
              {channelStatus && (
                <Chip
                  label={channelStatus.sms ? "On" : "Off"}
                  color={channelStatus.sms ? "success" : "default"}
                  size="small"
                  sx={{ ml: 1 }}
                />
              )}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="center">
              Locked
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6}>
                <LinearProgress />
              </TableCell>
            </TableRow>
          ) : (
            Object.values(settings).map((section) => {
              const items = Object.values(section.items);
              return (
                <Fragment key={section.id}>
                  {items.map((item, index) => (
                    <TableRow key={item.id}>
                      {index === 0 && (
                        <TableCell
                          sx={{ fontWeight: "bold" }}
                          rowSpan={items.length}
                        >
                          {section.label}
                        </TableCell>
                      )}
                      <TableCell>{item.label}</TableCell>
                      <TableCell align="center">
                        {displayChannelIcon(item.email)}
                      </TableCell>
                      <TableCell align="center">
                        {displayChannelIcon(item.push)}
                      </TableCell>
                      <TableCell align="center">
                        {displayChannelIcon(item.sms)}
                      </TableCell>
                      <TableCell align="center">
                        {item.locked ? (
                          <CheckCircleIcon color="primary" />
                        ) : (
                          <CancelIcon color="error" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </Fragment>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default OrgNotificationsTab;
